import * as stylex from "@stylexjs/stylex";
import { springConfigs } from "charts/lib/motion";
import { focusArticles } from "focus";
import { getFocusMetadata } from "focus-content";
import { AnimatePresence, motion } from "framer-motion";
import useLocale from "hooks/useLocale";
import { Article16 } from "icons/Article";
import { Book32 } from "icons/Book";
import Link from "next/link";
import React, { forwardRef, useState } from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useAppReducer } from "../hooks/useAppState";
import * as M from "../materials";
import { colors, zIndex } from "../materials/consts.stylex";
import { gridCenteredMaxWidthAreas, layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { articleAliases, foldRAliasArticle_, foldRAliasTheme_, getAliasHref, subthemesIndex, themeAliases } from "../routes";
const stylexStyles = {
    root: {
        kWkggS: "xdutcdc",
        kVAEAm: "x10l6tqk",
        kzqmXN: "xh8yej3",
        kskxy: "x11zsw2x",
        kY2c9j: "xb3hw6p",
        kGVxlE: "xlopqva",
        $$css: true
    }
};
export const expandHeightMotionProps = {
    variants: {
        show: {
            opacity: 1,
            height: "auto",
            transition: {
                staggerChildren: 0.06
            }
        },
        hidden: {
            opacity: 1,
            height: 0,
            transition: {
                delay: 0.2,
                staggerChildren: 0.03,
                staggerDirection: -1
            }
        }
    },
    initial: "hidden",
    animate: "show",
    exit: "hidden"
};
export const getThemeLabel = foldRAliasTheme_({
    access: ()=><FormattedMessage id="Access" defaultMessage="Access"/>,
    equity: ()=><FormattedMessage id="Equity" defaultMessage="Equity"/>,
    learning: ()=><FormattedMessage id="Learning" defaultMessage="Learning"/>,
    quality: ()=><FormattedMessage id="Quality" defaultMessage="Quality"/>,
    finance: ()=><FormattedMessage id="Finance" defaultMessage="Finance"/>
});
export const getLabel = foldRAliasArticle_({
    access: ()=><FormattedMessage id="Access" defaultMessage="Access"/>,
    equity: ()=><FormattedMessage id="Equity" defaultMessage="Equity"/>,
    learning: ()=><FormattedMessage id="Learning outcomes" defaultMessage="Learning outcomes"/>,
    trajectories: ()=><FormattedMessage id="Learning trajectories" defaultMessage="Learning trajectories"/>,
    quality: ()=><FormattedMessage id="Quality" defaultMessage="Quality"/>,
    finance: ()=><FormattedMessage id="Finance – Governments and households" defaultMessage="Finance – Governments and households"/>,
    "finance-aid": ()=><FormattedMessage id="Finance – Aid" defaultMessage="Finance – Aid"/>
});
const slideMotionProps = {
    variants: {
        show: {
            opacity: 1,
            y: "0%"
        },
        hidden: {
            opacity: 0,
            y: "-55%"
        }
    },
    transition: springConfigs.gentle
};
export const ArticleSwitcher = ()=>{
    const locale = useLocale();
    const [state, dispatch] = useAppReducer();
    const [hoveredTheme, setHoveredTheme] = useState(null);
    const [hoveredContent, setHoveredContent] = useState(null);
    const listRef = React.useRef(null);
    const lastIndex = articleAliases.length - 1;
    React.useEffect(()=>{
        setHoveredTheme(null);
        setHoveredContent(null);
        if (typeof document !== "undefined") {
            const el = listRef.current;
            const origin = document ? document.activeElement : null;
            if (state.themeDrawer.isOpen && el) {
                el.focus();
            }
            return ()=>{
                /** return focus to origin */ origin && origin.id === "article-switcher-toggle" && origin.focus();
            };
        } else return;
    }, [
        state.themeDrawer.isOpen
    ]);
    const subthemes = hoveredTheme ? subthemesIndex[hoveredTheme] : undefined;
    const hasSubthemes = subthemes && subthemes.length > 1;
    const focuses = React.useMemo(()=>focusArticles.published.map((a)=>getFocusMetadata(a, locale)).filter((a)=>subthemes?.includes(a.article)), [
        locale,
        subthemes
    ]);
    const content = [
        hasSubthemes ? [
            ...subthemes?.map((a)=>({
                    alias: a,
                    colors: M.themes[a],
                    title: getLabel(a),
                    icon: Book32,
                    type: "subtheme"
                }))
        ] : [],
        ...focuses.map((f)=>({
                alias: f.alias,
                colors: M.themes[f.article],
                title: f.title,
                icon: Article16,
                type: "focus"
            }))
    ].flat();
    return <AnimatePresence initial={false} key="themeDrawer">
      {state.themeDrawer.isOpen && <motion.div {...stylex.props(layout.gridCenteredMaxWidth, stylexStyles.root)} {...expandHeightMotionProps}>
          <div className="x1yedbjw xrvj5dj xtf6sjj x1ns3s31 xn2ks54 x1t1ogtf xb3r6kr">
            <ul className="x78zum5 xdt5ytf xou54vl xe8ttls x1odjw0f" role="menu" id="article-switcher">
              {themeAliases.map((alias, i)=>{
        return <motion.li key={alias} onMouseEnter={()=>setHoveredTheme(alias)} {...slideMotionProps}>
                    <ThemeCard key={alias} ref={i === 0 ? listRef : null} alias={alias} active={hoveredTheme === alias} dimmed={hoveredTheme !== null && hoveredTheme !== alias} onClick={()=>dispatch({
                type: "themeDrawer.close"
            })} onKeyUp={()=>{
            lastIndex === i && dispatch({
                type: "themeDrawer.close"
            });
        }}/>
                  </motion.li>;
    })}
            </ul>

            <div {...{
        0: {
            className: "x9t3ola xe0pwq x19ypqd9 x1wnbtiq x3onkmb x1jk6wm0"
        },
        1: {
            className: "x9t3ola xe0pwq x19ypqd9 x1wnbtiq x1jk6wm0 xg01cxk"
        }
    }[!!!(hoveredTheme && content.length > 0) << 0]}/>

            <ul className="x78zum5 xdt5ytf xou54vl xe8ttls x1odjw0f" role="menu" id="subarticle-switcher">
              {content.length > 0 && content.map((c, i)=>{
        return <motion.li key={c.alias} onMouseEnter={()=>setHoveredContent(c.alias)} onMouseLeave={()=>setHoveredContent(null)} {...slideMotionProps}>
                      <ContentCard type={c.type} alias={c.alias} colors={c.colors} title={c.title} icon={c.icon} active={hoveredContent === c.alias} dimmed={hoveredContent !== null && hoveredContent !== c.alias} onClick={()=>dispatch({
                type: "themeDrawer.close"
            })} onKeyUp={()=>{
            lastIndex === i && dispatch({
                type: "themeDrawer.close"
            });
        }} variants={{
            show: {
                y: 0
            },
            hidden: i > 0 ? {
                y: 10
            } : {
                y: -10
            }
        }}/>
                    </motion.li>;
    })}
            </ul>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
const ThemeCard = forwardRef(({ alias, active, dimmed, onClick, onKeyUp, ...props }, ref)=>{
    const theme = M.themes[alias];
    const imageUrl = theme.kind === "article" ? theme.illustration : null;
    return <motion.div className="x1ypdohk x12oqio5 xmtjor9" animate={{
        opacity: dimmed ? 0.8 : 1,
        backgroundColor: active ? theme.background.light : M.palette.brand.blue.main,
        color: active ? theme.textColor : M.whiteText,
        zIndex: active ? 1 : 0
    }} transition={{
        duration: 0.25,
        ease: "easeInOut"
    }} {...props}>
        <Link href={`/articles/${alias}`} ref={ref} id={`themedrawer-${alias}`} role="menuitem" tabIndex={0} onClick={onClick} onKeyUp={onKeyUp} className="x78zum5 x6s0dn4 x5yr21d xh8yej3 x1ll7tuh x1heor9g x1665zp3">
          {imageUrl && <img src={imageUrl} alt="" className="x1peatla"/>}
          <div className="x78zum5 xdt5ytf x1jnr06f">
            <span {...stylex.props(typography.heading3)}>{getThemeLabel(alias)}</span>
          </div>
        </Link>
      </motion.div>;
});
const ContentCard = ({ alias, colors, title, icon, type, active, dimmed, onClick, onKeyUp, ...props })=>{
    const intl = useIntl();
    const Icon = icon;
    const label = type === "subtheme" ? intl.formatMessage(defineMessage({
        id: "Sub-theme",
        defaultMessage: "Sub-theme"
    })) : intl.formatMessage(defineMessage({
        id: "Focus",
        defaultMessage: "Focus"
    }));
    return <motion.div className="x1ypdohk x12oqio5 xmtjor9" animate={{
        opacity: dimmed ? 0.8 : 1,
        backgroundColor: active ? colors.background.light : M.palette.brand.blue.main,
        color: active ? colors.textColor : M.whiteText,
        zIndex: active ? 1 : 0
    }} transition={{
        duration: 0.25,
        ease: "easeInOut"
    }} {...props}>
      <Link href={getAliasHref(alias)} id={`themedrawer-content-${alias}`} role="menuitem" tabIndex={0} onClick={onClick} onKeyUp={onKeyUp} className="x78zum5 x6s0dn4 x5yr21d xh8yej3 x1ll7tuh x1heor9g xou54vl">
        <div className="x1td3qas x10w6t97">
          <Icon className="x1td3qas x10w6t97"/>
        </div>

        <div className="x78zum5 xdt5ytf">
          <span {...stylex.props(typography.table)} style={{
        color: active ? colors.textColor : M.grayscalePalette[5]
    }}>
            {label}
          </span>
          <span {...stylex.props(typography.heading3)}>{title}</span>
        </div>
      </Link>
    </motion.div>;
};
